import React, { useRef, useState, useEffect } from 'react';
import { GoogleMap, MarkerF, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import app from '../../core/constants';
import { schoolTypeOptions } from '../../core/utils/helpers';
import { School } from '../../core/school/types';

const containerStyle = {
    width: '100%',
    height: '600px',
};

interface Props {
    schools: School[];
    selectedSchool?: School | null;
    onSchoolClick?: (school: School) => void;
    showInfoWindow?: boolean;
    onInfoWindowClose?: () => void;
}

const MultiSchoolMap: React.FC<Props> = ({
    schools,
    selectedSchool,
    onSchoolClick,
    showInfoWindow = false,
    onInfoWindowClose,
}) => {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: app?.GOOGLE_MAPS_API_KEY || '',
    });

    const [infoWindowSchool, setInfoWindowSchool] = useState<School | null>(null);

    const getSchoolTypeLabel = (type?: string) => schoolTypeOptions.find((opt) => opt.value === type)?.label || type;

    // Update InfoWindow when selectedSchool changes and showInfoWindow is true
    useEffect(() => {
        if (showInfoWindow && selectedSchool) {
            setInfoWindowSchool(selectedSchool);
        } else if (!showInfoWindow) {
            setInfoWindowSchool(null);
        }
    }, [selectedSchool, showInfoWindow]);

    // Calculate center point from all schools
    const calculateMapCenter = () => {
        const schoolsWithCoords = schools.filter(school => school.latitude && school.longitude);

        if (schoolsWithCoords.length === 0) {
            return { lat: 55.1694, lng: 23.8813 }; // Lithuania center
        }

        if (selectedSchool && selectedSchool.latitude && selectedSchool.longitude) {
            return {
                lat: parseFloat(String(selectedSchool.latitude)),
                lng: parseFloat(String(selectedSchool.longitude)),
            };
        }

        const totalLat = schoolsWithCoords.reduce((sum, school) => sum + parseFloat(String(school.latitude)), 0);
        const totalLng = schoolsWithCoords.reduce((sum, school) => sum + parseFloat(String(school.longitude)), 0);

        return {
            lat: totalLat / schoolsWithCoords.length,
            lng: totalLng / schoolsWithCoords.length,
        };
    };

    // Store center in state to prevent auto-focus on hover
    const [center, setCenter] = useState(calculateMapCenter());
    useEffect(() => {
        setCenter(calculateMapCenter());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schools, selectedSchool]);

    const handleMarkerClick = (school: School) => {
        setInfoWindowSchool(school);
        if (onSchoolClick) {
            onSchoolClick(school);
        }
    };

    const handleInfoWindowClose = () => {
        setInfoWindowSchool(null);
        if (onInfoWindowClose) {
            onInfoWindowClose();
        }
    };

    if (!isLoaded) return <div>Įkeliama žemėlapis...</div>;

    const schoolsWithCoords = schools.filter(school => school.latitude && school.longitude);

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={schoolsWithCoords.length > 1 ? 10 : 15}>
            {schoolsWithCoords.map((school) => {
                const lat = parseFloat(String(school.latitude));
                const lng = parseFloat(String(school.longitude));
                const isSelected = selectedSchool?.id === school.id;
                const typeColor = schoolTypeOptions.find(opt => opt.value === school.type)?.color || '#fdab01';

                return (
                    <MarkerF
                        key={school.id}
                        position={{ lat, lng }}
                        onMouseOver={() => setInfoWindowSchool(school)}
                        onClick={() => handleMarkerClick(school)}
                        icon={{
                            url: isSelected
                                ? `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="14" fill="#fff" stroke="#fff" stroke-width="2"/>
                                        <circle cx="16" cy="16" r="12" fill="${typeColor}" stroke="#fff" stroke-width="2"/>
                                        <circle cx="16" cy="16" r="6" fill="#fff"/>
                                    </svg>
                                `)}`
                                : `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#fff" stroke="#fff" stroke-width="2"/>
                                        <circle cx="12" cy="12" r="8" fill="${typeColor}" stroke="#fff" stroke-width="2"/>
                                    </svg>
                                `)}`,
                            scaledSize: new window.google.maps.Size(isSelected ? 32 : 24, isSelected ? 32 : 24),
                        }} />
                );
            })}

            {infoWindowSchool && (
                <InfoWindowF
                    position={{
                        lat: parseFloat(String(infoWindowSchool.latitude)),
                        lng: parseFloat(String(infoWindowSchool.longitude)),
                    }}
                    onCloseClick={handleInfoWindowClose}
                    options={{
                        pixelOffset: new window.google.maps.Size(0, -30),
                    }}>
                    <div style={{ maxWidth: 250 }}>
                        <h4 style={{ marginBottom: 5 }}>{infoWindowSchool.name}</h4>
                        {infoWindowSchool.type && (
                            <p style={{ margin: 0 }}>
                                <strong>Tipas:</strong> {getSchoolTypeLabel(infoWindowSchool.type)}
                            </p>
                        )}
                        {infoWindowSchool.address && (
                            <p style={{ margin: 0 }}>
                                <strong>Adresas:</strong> {infoWindowSchool.address}
                            </p>
                        )}
                        {infoWindowSchool.phone && (
                            <p style={{ margin: 0 }}>
                                <strong>Telefonas:</strong> {infoWindowSchool.phone}
                            </p>
                        )}
                    </div>
                </InfoWindowF>
            )}
        </GoogleMap>
    );
};

MultiSchoolMap.defaultProps = {
    selectedSchool: null,
    onSchoolClick: () => null,
    showInfoWindow: false,
    onInfoWindowClose: () => null,
};

export default MultiSchoolMap;
